import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Briefcase, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioManagement = ({ user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file || !selectedClient) {
      toast.error('Please select a client and file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/portfolios/upload?client_id=${selectedClient}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Portfolio uploaded successfully');
      setDialogOpen(false);
      setFile(null);
      setSelectedClient('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload portfolio');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="portfolio-management-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Portfolio Management
            </h1>
            <p className="text-gray-600">Upload and manage client portfolios</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="upload-portfolio-button">
                <Upload className="w-4 h-4 mr-2" />
                Upload Portfolio
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
              <DialogHeader>
                <DialogTitle>Upload Portfolio Data</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleFileUpload} className="space-y-4">
                <div>
                  <Label htmlFor="client" className="text-gray-700">Select Client *</Label>
                  <select
                    id="client"
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    required
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="client-select"
                  >
                    <option value="">Choose a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_name} - {client.type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="file" className="text-gray-700">Upload File (PDF, Excel) *</Label>
                  <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#1a1a1a]/50 transition-colors">
                    <input
                      type="file"
                      id="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                      {file ? (
                        <p className="text-gray-900">{file.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-900 mb-1">Click to upload or drag and drop</p>
                          <p className="text-gray-500 text-sm">PDF, XLSX, or XLS (max 10MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" disabled={uploading} data-testid="submit-upload-button">
                  {uploading ? 'Uploading...' : 'Upload Portfolio'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-gray-200 bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-3">
                <Upload className="w-6 h-6 text-ugmonk-dark" />
                Upload Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-gray-900 font-semibold mb-2">File Upload</h4>
                <p className="text-gray-600 text-sm mb-3">Upload portfolio data from PDF or Excel files with AI-powered parsing</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="file-upload-option-button">
                  Upload File
                </Button>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-gray-900 font-semibold mb-2">API Integration</h4>
                <p className="text-gray-600 text-sm mb-3">Connect to Wealth Spectrum for automated data import</p>
                <Button variant="outline" className="border-[#1a1a1a] text-ugmonk-dark" disabled>
                  Coming Soon
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200 bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-3">
                <Briefcase className="w-6 h-6 text-ugmonk-dark" />
                Portfolio Data Format
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-ugmonk-dark font-semibold mb-1">Holdings Data</p>
                  <p className="text-gray-600">Security name, Quantity, Current value, Asset class</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-ugmonk-dark font-semibold mb-1">Transaction Data</p>
                  <p className="text-gray-600">Date, Security, Transaction type, Amount</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-ugmonk-dark font-semibold mb-1">Supported Formats</p>
                  <p className="text-gray-600">PDF (scanned/digital), Excel (.xlsx, .xls)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PortfolioManagement;
